import { CampaignGoal } from 'api/models'
import { AccountDefaultOrientation } from 'domain/googleConversionGoals/schemas'

// Vocabulary for `get_default_bidding_strategy`'s own `objective` param
// (marketing_integration_platform/app/integration/google/defaults.py) —
// distinct from the FE's CampaignGoal enum (UTILIZATION/ROAS/VISIBILITY).
export const GoogleObjective = Object.freeze({
  awareness: 'awareness',
  traffic: 'traffic',
  sales: 'sales',
  leads: 'leads',
  // FE-only: the backend maps the ROAS goal onto `sales`, whose default
  // maximizes conversion count rather than value. Never sent anywhere.
  roas: 'roas',
})

export const GoogleBiddingStrategyType = Object.freeze({
  manualCpm: 'MANUAL_CPM',
  maximizeClicks: 'MAXIMIZE_CLICKS',
  maximizeConversions: 'MAXIMIZE_CONVERSIONS',
  maximizeConversionValue: 'MAXIMIZE_CONVERSION_VALUE',
  targetSpend: 'TARGET_SPEND',
  targetCpa: 'TARGET_CPA',
  targetRoas: 'TARGET_ROAS',
})

export const SELECTABLE_BIDDING_STRATEGY_TYPES = Object.freeze([
  GoogleBiddingStrategyType.manualCpm,
  GoogleBiddingStrategyType.targetSpend,
  GoogleBiddingStrategyType.maximizeConversions,
  GoogleBiddingStrategyType.maximizeConversionValue,
  GoogleBiddingStrategyType.targetRoas,
])

// true if a conversion action is set or the account is purchase/roas oriented
// (in which case, account default conversion goals are used)
export function resolveHasConversionAction({
  explicitSelectionCount,
  accountDefaultOrientation,
  campaignGoal,
}) {
  if (explicitSelectionCount > 0) return true
  if (campaignGoal === CampaignGoal.roas) {
    return accountDefaultOrientation === AccountDefaultOrientation.purchase
  }
  if (campaignGoal === CampaignGoal.utilization) {
    return (
      accountDefaultOrientation === AccountDefaultOrientation.purchase ||
      accountDefaultOrientation === AccountDefaultOrientation.lead
    )
  }
  return false
}

export function getSuitableBiddingStrategyTypes({
  campaignGoal,
  apiCampaignType,
  hasConversionAction,
}) {
  if (campaignGoal === CampaignGoal.roas) {
    return hasConversionAction
      ? [
          GoogleBiddingStrategyType.targetSpend,
          GoogleBiddingStrategyType.maximizeConversionValue,
          GoogleBiddingStrategyType.targetRoas,
        ]
      : [GoogleBiddingStrategyType.targetSpend]
  }

  if (campaignGoal === CampaignGoal.utilization) {
    return hasConversionAction
      ? [GoogleBiddingStrategyType.targetSpend, GoogleBiddingStrategyType.maximizeConversions]
      : [GoogleBiddingStrategyType.targetSpend]
  }

  if (campaignGoal === CampaignGoal.visibility) {
    return apiCampaignType === 'DISPLAY'
      ? [GoogleBiddingStrategyType.manualCpm, GoogleBiddingStrategyType.targetSpend]
      : [GoogleBiddingStrategyType.targetSpend]
  }

  return SELECTABLE_BIDDING_STRATEGY_TYPES
}

export const BiddingTargetValueField = Object.freeze({
  cpmBidMicros: 'cpm_bid_micros',
  targetRoas: 'target_roas',
  cpcBidCeilingMicros: 'cpc_bid_ceiling_micros',
})

// Which single numeric input (if any) a given strategy needs, and where its
// value is written
export function targetValueFieldForStrategy(biddingStrategyType) {
  if (biddingStrategyType === GoogleBiddingStrategyType.manualCpm) {
    return BiddingTargetValueField.cpmBidMicros
  }
  if (biddingStrategyType === GoogleBiddingStrategyType.targetRoas) {
    return BiddingTargetValueField.targetRoas
  }
  if (
    biddingStrategyType === GoogleBiddingStrategyType.targetSpend ||
    biddingStrategyType === GoogleBiddingStrategyType.maximizeClicks
  ) {
    return BiddingTargetValueField.cpcBidCeilingMicros
  }
  return null
}

// TARGET_ROAS target value is required
export function isBiddingTargetValueMissing({ biddingStrategyType, targetRoas }) {
  return biddingStrategyType === GoogleBiddingStrategyType.targetRoas && targetRoas == null
}

const MICROS_PER_UNIT = 1000000

// cpm_bid_micros micros <-> normal currency
export function microsToCurrencyAmount(micros) {
  return micros == null ? null : micros / MICROS_PER_UNIT
}

export function currencyAmountToMicros(amount) {
  return amount == null ? null : Math.round(amount * MICROS_PER_UNIT)
}

// target_roas ratio (server-side, e.g. 2.5 = 250%) <-> percentage.
const ROAS_PRECISION = 1e6

function roundRoas(value) {
  return Math.round(value * ROAS_PRECISION) / ROAS_PRECISION
}

export function ratioToPercentage(ratio) {
  return ratio == null ? null : roundRoas(ratio * 100)
}

export function percentageToRatio(percentage) {
  return percentage == null ? null : roundRoas(percentage / 100)
}

export function resolveDefaultBiddingStrategyType(
  apiCampaignType,
  apiObjective,
  hasConversionAction,
) {
  if (apiCampaignType === 'DISPLAY') {
    if (apiObjective === GoogleObjective.awareness) return GoogleBiddingStrategyType.manualCpm
    if (apiObjective === GoogleObjective.traffic) return GoogleBiddingStrategyType.targetSpend
    if (apiObjective === GoogleObjective.sales || apiObjective === GoogleObjective.leads) {
      return hasConversionAction
        ? GoogleBiddingStrategyType.maximizeConversions
        : GoogleBiddingStrategyType.targetSpend
    }
    if (apiObjective === GoogleObjective.roas) {
      return hasConversionAction
        ? GoogleBiddingStrategyType.maximizeConversionValue
        : GoogleBiddingStrategyType.targetSpend
    }
  }

  if (apiCampaignType === 'VIDEO_CONVERSIONS') {
    if (apiObjective === GoogleObjective.awareness) return GoogleBiddingStrategyType.targetSpend
    if (apiObjective === GoogleObjective.traffic) return GoogleBiddingStrategyType.targetSpend
    if (apiObjective === GoogleObjective.sales || apiObjective === GoogleObjective.leads) {
      return hasConversionAction
        ? GoogleBiddingStrategyType.maximizeConversions
        : GoogleBiddingStrategyType.targetSpend
    }
    if (apiObjective === GoogleObjective.roas) {
      return hasConversionAction
        ? GoogleBiddingStrategyType.maximizeConversionValue
        : GoogleBiddingStrategyType.targetSpend
    }
  }

  return null
}

export function mapCampaignGoalToGoogleObjective(campaignGoal, { hasLeadForm = false } = {}) {
  if (campaignGoal === CampaignGoal.visibility) return GoogleObjective.awareness
  if (campaignGoal === CampaignGoal.roas) return GoogleObjective.roas
  if (campaignGoal === CampaignGoal.utilization) {
    return hasLeadForm ? GoogleObjective.leads : GoogleObjective.sales
  }
  return null
}

export function resolveGoogleBiddingStrategyDefault({
  campaignGoal,
  apiCampaignType,
  hasConversionAction,
  hasLeadForm,
}) {
  const objective = mapCampaignGoalToGoogleObjective(campaignGoal, { hasLeadForm })
  if (!objective) return null
  return resolveDefaultBiddingStrategyType(apiCampaignType, objective, hasConversionAction)
}
